import React, { useEffect, useRef, useCallback, Fragment } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PostType } from '../memeverse/memeverse_types';
import { getUsersMyPosts } from '../../utils/apis/memeverse';
import Meme from '../memeverse/feed/Meme';
import { useAuth } from '../../hooks/useAuth';
import './../memeverse/feed/MemeReply.css';
import EmptyState from './EmptyState';
import MemeInProfilePage from '../memeverse/feed/MemeInProfilePage';

const MvPosts = ({ userId, onReplyClick }) => {
  const { authUser } = useAuth();

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteQuery({
      queryKey: ['userPosts', userId],
      queryFn: async ({ pageParam = 1 }) => {
        try {
          const resp = await getUsersMyPosts(
            authUser?.uid,
            userId,
            10,
            pageParam
          );
          if (resp.ok) {
            const data = await resp.json();
            return {
              posts: data.posts as PostType[],
              pagination: data.pagination,
            };
          }
          return { posts: [], pagination: null };
        } catch (err) {
          console.error('Error fetching posts:', err);
          return { posts: [], pagination: null };
        }
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.has_next
          ? lastPage.pagination.next_page
          : undefined,
    });

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, isFetching, fetchNextPage]
  );

  const posts = data?.pages.flatMap((page) => page.posts) || [];

  return (
    <>
      {posts.length === 0 ? (
        isLoading ? (
          <Box
            display='flex'
            flexDirection='column'
            flex='1'
            flexGrow='1'
            width='100%'
            justifyContent='center'
            alignItems='center'
          >
            <CircularProgress />
          </Box>
        ) : (
          <EmptyState
            primaryMessage='No posts, yet.'
            secondaryMessage='You will see a record of your posts here.'
            buttonTitle='Create Post on Ask'
            redirectUrl='/ask'
          />
        )
      ) : (
        <Box
          sx={{
            width: '100%',
            maxWidth: 'md',
            margin: 'auto',
            my: 4,
            gap: 2,
          }}
        >
          <>
            {posts.map((post, index) => (
              <Fragment key={`${post.docId}-${index}`}>
                <MemeInProfilePage post={post} onReplyClick={onReplyClick} />
                <Divider sx={{ my: 1 }} />
              </Fragment>
            ))}

            {hasNextPage && (
              <Box
                ref={loadMoreTriggerRef}
                sx={{
                  height: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {isFetching && <CircularProgress size={24} />}
              </Box>
            )}
          </>
        </Box>
      )}
    </>
  );
};

export default MvPosts;
